/**
 * Selbstdiagnose für Browser-/OS-Benachrichtigungen — bedienbar aus der
 * Browser-Konsole über `kmvsPush`.
 *
 * Warum eigenes Werkzeug: Der Weg einer Mitteilung führt über sechs Stationen
 * (Backend → Notification-Zeile → Realtime/Poll → Mitteilungszentrum sowie
 * Backend → Push-Dienst → Service Worker → Betriebssystem). Fällt eine aus,
 * sieht der Nutzer schlicht „nichts“ — ohne Fehlermeldung. Jede Station wird
 * hier einzeln geprüft und benannt.
 *
 *   kmvsPush.hilfe()          Übersicht der Befehle
 *   kmvsPush.pruefen()        Alle Stationen prüfen, Befund ausgeben
 *   kmvsPush.test()           Echte Testmitteilung über den Produktivpfad
 *   kmvsPush.lokalerTest()    Nur Service Worker + Betriebssystem prüfen
 *   kmvsPush.erlauben()       Berechtigung anfragen und abonnieren
 *   kmvsPush.reparieren()     Abo verwerfen und frisch anlegen
 */
import { apiUrl } from "../utils/api";
import { pushService } from "./pushService";
import { MENTION_TYPES } from "../components/notifications/notifIcon";

/* ────────── Typen der Serverantworten (siehe notification.controller.ts) ────────── */

export type ServerAbo = {
  id: number;
  anbieter: string;
  endpoint: string;
  angelegt: string;
  alterTage: number;
};

export type ServerDiagnose = {
  userId: string;
  tenantId: string;
  serverZeit: string;
  push: {
    aktiv: boolean;
    grund: string | null;
    vapidPublicKeyAnfang: string | null;
    vapidPublicKeyLaenge: number | null;
    abos: ServerAbo[];
  };
  mitteilungen: {
    ungelesenGesamt: number;
    ungelesenNachTyp: { type: string; ungelesen: number }[];
  };
};

export type PushZustellung = {
  subscriptionId: number;
  anbieter: string;
  endpoint: string;
  ok: boolean;
  statusCode: number | null;
  fehler: string | null;
  entfernt: boolean;
};

export type ServerTestErgebnis = {
  angelegt: number[];
  push: { aktiv: boolean; grund: string | null; ergebnisse: PushZustellung[] };
};

/** Eine geprüfte Station: bestanden, mit Wert und ggf. Hinweis. */
export type Befund = {
  station: string;
  ok: boolean | null; // null = nicht zutreffend / unbekannt
  wert: string;
  hinweis?: string;
};

export type Diagnose = {
  befunde: Befund[];
  probleme: string[];
  server: ServerDiagnose | null;
  roh: Record<string, unknown>;
};

/* ────────── Hilfsmittel ────────── */

const STIL_OK = "color:#137333;font-weight:600";
const STIL_FEHLER = "color:#c5221f;font-weight:600";
const STIL_NEUTRAL = "color:#8a8a8a";
const STIL_TITEL = "color:#7d2169;font-weight:700;font-size:13px";

const zeichen = (ok: boolean | null): string => (ok === null ? "•" : ok ? "✔" : "✖");
const stil = (ok: boolean | null): string => (ok === null ? STIL_NEUTRAL : ok ? STIL_OK : STIL_FEHLER);

const base64UrlAus = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binaer = "";
  for (let i = 0; i < bytes.length; i++) binaer += String.fromCharCode(bytes[i]);
  return btoa(binaer).replace(/=+$/g, "").replace(/\+/g, "-").replace(/\//g, "_");
};

const hostVon = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return "unbekannt";
  }
};

/** Endpoint gekürzt wie serverseitig: Host + „…“ + letzte 12 Zeichen. */
const kuerzeEndpoint = (endpoint: string): string => `${hostVon(endpoint)}/…${endpoint.slice(-12)}`;

/** Browserfamilie — bestimmt, welche Eigenheiten gelten. */
const browserArt = (): "safari" | "firefox" | "chromium" | "unbekannt" => {
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Chrome\/|Chromium\/|Edg\//.test(ua)) return "chromium";
  if (/Safari\//.test(ua)) return "safari";
  return "unbekannt";
};

const istIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const alsPwaInstalliert = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

async function serverDiagnose(): Promise<ServerDiagnose | null> {
  try {
    const res = await fetch(apiUrl("notifications/diagnose"));
    if (!res.ok) {
      console.warn(`[kmvsPush] Server-Diagnose fehlgeschlagen: HTTP ${res.status}`);
      return null;
    }
    return (await res.json()) as ServerDiagnose;
  } catch (err) {
    console.warn("[kmvsPush] Server-Diagnose nicht erreichbar:", err);
    return null;
  }
}

/* ────────── Die Prüfung ────────── */

async function pruefen(): Promise<Diagnose> {
  const befunde: Befund[] = [];
  const probleme: string[] = [];
  const roh: Record<string, unknown> = {};

  // 1) Kontext: ohne HTTPS (oder localhost) gibt es weder Service Worker noch Push.
  const sicher = window.isSecureContext;
  befunde.push({
    station: "Sicherer Kontext (HTTPS)",
    ok: sicher,
    wert: window.location.origin,
    hinweis: sicher
      ? undefined
      : "Ohne HTTPS (Ausnahme: localhost) stellt der Browser weder Service Worker noch Push bereit. Über eine http://-Adresse sind Benachrichtigungen technisch unmöglich.",
  });
  if (!sicher) probleme.push("Die Seite läuft nicht in einem sicheren Kontext (HTTPS) — Push ist dort vom Browser gesperrt.");
  roh.origin = window.location.origin;
  roh.isSecureContext = sicher;

  // 2) Browser-Fähigkeiten.
  const hatSW = "serviceWorker" in navigator;
  const hatPushManager = typeof window !== "undefined" && "PushManager" in window;
  const hatNotification = typeof window !== "undefined" && "Notification" in window;
  befunde.push({ station: "Service Worker verfügbar", ok: hatSW, wert: String(hatSW) });
  befunde.push({ station: "PushManager verfügbar", ok: hatPushManager, wert: String(hatPushManager) });
  befunde.push({ station: "Notification-API verfügbar", ok: hatNotification, wert: String(hatNotification) });
  if (!hatPushManager && istIOS() && !alsPwaInstalliert()) {
    probleme.push(
      "iOS/iPadOS liefert Web-Push NUR in einer zum Home-Bildschirm hinzugefügten Web-App. Im normalen Safari-Tab gibt es keine Benachrichtigungen.",
    );
  } else if (!hatSW || !hatPushManager) {
    probleme.push("Dieser Browser stellt Service Worker oder PushManager nicht bereit (privates Fenster? Richtlinie?).");
  }

  // 3) Berechtigung.
  const erlaubnis = hatNotification ? Notification.permission : "n/a";
  befunde.push({
    station: "Berechtigung (Browser)",
    ok: erlaubnis === "granted" ? true : erlaubnis === "denied" ? false : null,
    wert: erlaubnis,
    hinweis:
      erlaubnis === "denied"
        ? "Blockiert. Der Browser fragt nicht erneut — die Erlaubnis muss in den Seiteneinstellungen (Schloss-Symbol links in der Adressleiste) auf „Zulassen“ gesetzt werden."
        : erlaubnis === "default"
          ? "Noch nicht gefragt. kmvsPush.erlauben() ausführen (oder im Mitteilungszentrum die Glocke anklicken)."
          : undefined,
  });
  if (erlaubnis === "denied") probleme.push("Die Benachrichtigungs-Erlaubnis ist für diese Seite blockiert.");
  if (erlaubnis === "default") probleme.push("Die Benachrichtigungs-Erlaubnis wurde nie erteilt — es kann keine Push ankommen.");
  roh.permission = erlaubnis;

  // 4) Service-Worker-Registrierung.
  let registrierung: ServiceWorkerRegistration | undefined;
  if (hatSW) {
    registrierung = await navigator.serviceWorker.getRegistration();
    const aktiv = registrierung?.active?.state === "activated";
    befunde.push({
      station: "Service Worker registriert",
      ok: !!registrierung,
      wert: registrierung ? `${registrierung.active?.scriptURL ?? "(kein aktives Skript)"} · Scope ${registrierung.scope}` : "keine Registrierung",
      hinweis: registrierung
        ? undefined
        : "Ohne registrierten Service Worker kann der Browser keine Push entgegennehmen. Seite neu laden; bleibt es dabei, wird /sw.js nicht ausgeliefert.",
    });
    befunde.push({
      station: "Service Worker aktiv",
      ok: aktiv,
      wert: registrierung?.active?.state ?? "kein active-Worker",
      hinweis: aktiv ? undefined : "Der Worker ist registriert, aber nicht aktiviert (installing/waiting). Seite einmal neu laden.",
    });
    if (!registrierung) probleme.push("Es ist kein Service Worker registriert (/sw.js).");
    roh.serviceWorker = registrierung
      ? { scope: registrierung.scope, script: registrierung.active?.scriptURL, state: registrierung.active?.state }
      : null;
  }

  // 5) Abo im Browser + Abgleich mit dem Server-Schlüssel.
  const server = await serverDiagnose();
  let abo: PushSubscription | null = null;
  if (registrierung && hatPushManager) {
    abo = await registrierung.pushManager.getSubscription();
    befunde.push({
      station: "Push-Abo im Browser",
      ok: !!abo,
      wert: abo ? kuerzeEndpoint(abo.endpoint) : "kein Abo",
      hinweis: abo ? undefined : "Der Browser hat kein Abo. kmvsPush.erlauben() ausführen.",
    });
    if (!abo && erlaubnis === "granted") {
      probleme.push("Die Erlaubnis ist erteilt, aber es besteht kein Push-Abo — kmvsPush.reparieren() legt es neu an.");
    }
    roh.subscription = abo ? { endpoint: abo.endpoint, expirationTime: abo.expirationTime } : null;
  }

  // 6) Schlüsselabgleich: Ein Abo mit altem VAPID-Schlüssel wird vom Push-Dienst
  //    abgelehnt — der Browser meldet davon nichts.
  const serverKeyAnfang = server?.push.vapidPublicKeyAnfang ?? null;
  if (abo && serverKeyAnfang) {
    const aboKey = abo.options?.applicationServerKey ? base64UrlAus(abo.options.applicationServerKey) : null;
    const passt = aboKey ? aboKey.startsWith(serverKeyAnfang) : null;
    befunde.push({
      station: "Abo passt zum Server-Schlüssel (VAPID)",
      ok: passt,
      wert: aboKey ? `${aboKey.slice(0, 12)}… ↔ Server ${serverKeyAnfang}…` : "Abo nennt keinen Schlüssel",
      hinweis: passt === false ? "Das Abo wurde mit einem anderen VAPID-Schlüssel angelegt. kmvsPush.reparieren() erneuert es." : undefined,
    });
    if (passt === false) probleme.push("Das Push-Abo im Browser gehört zu einem alten VAPID-Schlüssel — der Push-Dienst weist Zustellungen ab.");
  }

  // 7) Serverseite.
  if (server) {
    befunde.push({
      station: "Web-Push serverseitig aktiv",
      ok: server.push.aktiv,
      wert: server.push.aktiv ? `VAPID ${server.push.vapidPublicKeyAnfang}… (${server.push.vapidPublicKeyLaenge} Zeichen)` : "deaktiviert",
      hinweis: server.push.aktiv ? undefined : `Grund: ${server.push.grund ?? "unbekannt"} — ohne VAPID-Schlüsselpaar sendet das Backend keine Push.`,
    });
    if (!server.push.aktiv) probleme.push(`Das Backend hat Web-Push deaktiviert (${server.push.grund ?? "unbekannt"}).`);

    const meinAbo = abo ? server.push.abos.find((a) => a.endpoint === kuerzeEndpoint(abo!.endpoint)) : undefined;
    befunde.push({
      station: "Abo beim Backend registriert",
      ok: abo ? !!meinAbo : null,
      wert: `${server.push.abos.length} Abo(s) für diesen Nutzer${meinAbo ? ", darunter dieses Gerät" : ""}`,
      hinweis:
        abo && !meinAbo
          ? "Der Browser hat ein Abo, das Backend kennt es aber nicht — POST /api/push/subscribe kam nie durch. kmvsPush.reparieren() meldet es erneut an."
          : server.push.abos.length > 3
            ? `${server.push.abos.length} Abos: jedes Gerät/Browser-Profil hinterlässt eines. Alte, nicht mehr benutzte laufen ins Leere, bis der Push-Dienst sie als „gone“ meldet.`
            : undefined,
    });
    if (abo && !meinAbo) probleme.push("Dieses Gerät ist im Backend nicht als Empfänger registriert.");

    // 8) Ungelesene Typen, die die Oberfläche gar nicht anzeigt.
    const unsichtbar = server.mitteilungen.ungelesenNachTyp.filter((t) => !wirdAngezeigt(t.type));
    if (unsichtbar.length) {
      const summe = unsichtbar.reduce((s, t) => s + t.ungelesen, 0);
      befunde.push({
        station: "Mitteilungen ohne Anzeige im Mitteilungszentrum",
        ok: false,
        wert: unsichtbar.map((t) => `${t.type}: ${t.ungelesen}`).join(", "),
        hinweis:
          "Diese Typen erzeugen eine Zeile in der Datenbank UND eine Browser-Push, erscheinen aber weder in der Glocke noch auf dem Dashboard (Filter MENTION_TYPES in notifIcon.tsx).",
      });
      probleme.push(
        `${summe} ungelesene Mitteilung(en) in Typen, die die Oberfläche ausblendet (${unsichtbar.map((t) => t.type).join(", ")}) — Push kommt an, im Mitteilungszentrum steht nichts.`,
      );
    }
  }

  // 9) Browser-Eigenheiten, die keine Fehlermeldung erzeugen.
  const art = browserArt();
  if (art === "safari" || istIOS()) {
    befunde.push({
      station: "Hinweis Safari/iOS",
      ok: null,
      wert: alsPwaInstalliert() ? "als Web-App installiert" : "im Browser-Tab",
      hinweis:
        "Safari fragt die Erlaubnis NUR direkt nach einem Klick. Auf iOS/iPadOS funktioniert Push ausschließlich in der zum Home-Bildschirm hinzugefügten Web-App.",
    });
  }
  if (art === "firefox") {
    befunde.push({
      station: "Hinweis Firefox",
      ok: null,
      wert: "Erlaubnis nur nach Klick",
      hinweis: "Firefox verwirft eine Berechtigungsanfrage ohne vorangehende Nutzer-Aktion stillschweigend.",
    });
  }
  befunde.push({
    station: "Hinweis Betriebssystem",
    ok: null,
    wert: document.hasFocus() ? "Fenster im Vordergrund" : "Fenster im Hintergrund",
    hinweis:
      "Auch bei erteilter Erlaubnis blendet das Betriebssystem nichts ein, wenn „Nicht stören“/„Fokus“ aktiv ist oder der Browser in den Systemeinstellungen keine Mitteilungen zeigen darf. Zugestellte Einblendungen landen im Mitteilungszentrum des Betriebssystems.",
  });
  roh.browser = { art, istIOS: istIOS(), alsPwaInstalliert: alsPwaInstalliert(), userAgent: navigator.userAgent };

  ausgeben("Benachrichtigungen — Prüfung", befunde, probleme);
  return { befunde, probleme, server, roh };
}

/** Mail-Typen erscheinen nicht einzeln, aber als aggregierte Zeile („X neue Mails“)
 *  im Mitteilungszentrum — sie gelten deshalb nicht als unsichtbar. */
const AGGREGIERT_ANGEZEIGT = new Set<string>(["mail_incoming", "mail_digest"]);

/** Erscheint dieser Typ irgendwo in der Oberfläche? `MENTION_TYPES` ist die
 *  einzige Quelle der Wahrheit für die Einzelanzeige (notifIcon.tsx). */
const wirdAngezeigt = (type: string): boolean => MENTION_TYPES.has(type) || AGGREGIERT_ANGEZEIGT.has(type);

function ausgeben(titel: string, befunde: Befund[], probleme: string[]): void {
  console.group(`%c${titel}`, STIL_TITEL);
  for (const b of befunde) {
    console.log(`%c${zeichen(b.ok)} %c${b.station}: %c${b.wert}`, stil(b.ok), "font-weight:600", "font-weight:400");
    if (b.hinweis) console.log(`   %c↳ ${b.hinweis}`, STIL_NEUTRAL);
  }
  if (probleme.length) {
    console.group("%cBefund", STIL_FEHLER);
    probleme.forEach((p, i) => console.log(`${i + 1}. ${p}`));
    console.groupEnd();
  } else {
    console.log("%c✔ Kein Problem gefunden — kmvsPush.test() schickt eine echte Testmitteilung.", STIL_OK);
  }
  console.groupEnd();
}

/* ────────── Aktionen ────────── */

/** Berechtigung anfragen (aus der Konsole heraus zulässig) und abonnieren. */
async function erlauben(): Promise<NotificationPermission> {
  if (!("Notification" in window)) {
    console.error("[kmvsPush] Dieser Browser kennt die Notification-API nicht.");
    return "denied";
  }
  if (Notification.permission === "denied") {
    console.error(
      "%c[kmvsPush] Die Erlaubnis ist blockiert. Der Browser fragt nicht erneut — im Schloss-Symbol der Adressleiste auf „Zulassen“ stellen und die Seite neu laden.",
      STIL_FEHLER,
    );
    return "denied";
  }
  const erlaubnis = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  console.log(`[kmvsPush] Erlaubnis: ${erlaubnis}`);
  if (erlaubnis === "granted") {
    await pushService.ensureSubscribed({ interactive: true });
    console.log("%c[kmvsPush] Abo angelegt/erneuert. kmvsPush.test() prüft die Zustellung.", STIL_OK);
  }
  return erlaubnis;
}

/** Bestehendes Abo verwerfen und frisch anlegen — hilft bei altem VAPID-Schlüssel
 *  oder wenn das Backend das Abo nicht kennt. */
async function reparieren(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    console.error("[kmvsPush] Kein Service Worker in diesem Browser.");
    return;
  }
  const reg = await navigator.serviceWorker.ready;
  const alt = await reg.pushManager.getSubscription();
  if (alt) {
    try {
      await fetch(apiUrl("push/unsubscribe"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint: alt.endpoint }),
      });
    } catch {
      /* Backend-Abmeldung ist Kür — der Endpoint verschwindet spätestens beim nächsten „gone“. */
    }
    await alt.unsubscribe();
    console.log(`[kmvsPush] Altes Abo verworfen (${kuerzeEndpoint(alt.endpoint)}).`);
  }
  await pushService.ensureSubscribed({ interactive: true });
  const neu = await reg.pushManager.getSubscription();
  console.log(
    neu ? `%c[kmvsPush] Neues Abo: ${kuerzeEndpoint(neu.endpoint)}` : "%c[kmvsPush] Es konnte kein Abo angelegt werden.",
    neu ? STIL_OK : STIL_FEHLER,
  );
}

/** Zeigt eine Einblendung DIREKT über den Service Worker — ohne Server und ohne
 *  Push-Dienst. Kommt sie an, sind Erlaubnis, Worker und Betriebssystem in Ordnung
 *  und der Fehler liegt weiter hinten (Abo, Backend, Push-Dienst). */
async function lokalerTest(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || Notification.permission !== "granted") {
    console.error("[kmvsPush] Erst kmvsPush.erlauben() ausführen (Erlaubnis fehlt oder kein Service Worker).");
    return false;
  }
  const reg = await navigator.serviceWorker.ready;
  await reg.showNotification("KMVS — lokaler Test", {
    body: "Diese Einblendung kam ohne Server zustande. Sie beweist: Erlaubnis, Service Worker und Betriebssystem funktionieren.",
    icon: "/logo192.png",
    badge: "/logo192.png",
    tag: "kmvs-lokaler-test",
    data: { link: "/" },
  });
  console.log(
    "%c[kmvsPush] Einblendung ausgelöst. Erscheint sie NICHT, blockiert das Betriebssystem sie (Fokus/Nicht stören, oder der Browser darf systemseitig keine Mitteilungen zeigen).",
    STIL_OK,
  );
  return true;
}

/** Echte Testmitteilung über den Produktivpfad: Backend legt die Zeile an
 *  (→ Mitteilungszentrum + Toast) und schickt die Push an alle Abos. */
async function test(): Promise<ServerTestErgebnis | null> {
  let ergebnis: ServerTestErgebnis;
  try {
    const res = await fetch(apiUrl("notifications/test"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      console.error(`%c[kmvsPush] Der Test-Endpunkt antwortete mit HTTP ${res.status}. Angemeldet?`, STIL_FEHLER);
      return null;
    }
    ergebnis = (await res.json()) as ServerTestErgebnis;
  } catch (err) {
    console.error("%c[kmvsPush] Der Test-Endpunkt ist nicht erreichbar:", STIL_FEHLER, err);
    return null;
  }

  const befunde: Befund[] = [];
  const probleme: string[] = [];

  befunde.push({
    station: "Mitteilungszentrum (Zeile angelegt)",
    ok: ergebnis.angelegt.length > 0,
    wert: ergebnis.angelegt.length ? `Notification #${ergebnis.angelegt.join(", #")}` : "keine Zeile",
    hinweis: ergebnis.angelegt.length
      ? "Sie muss binnen ~1 Sekunde in der Glocke und als Einblendung oben rechts stehen (spätestens nach 60 s durch den Poll)."
      : "Das Backend hat keine Zeile angelegt — Empfängerliste leer.",
  });
  if (!ergebnis.angelegt.length) probleme.push("Es wurde keine Mitteilung angelegt.");

  befunde.push({
    station: "Web-Push serverseitig",
    ok: ergebnis.push.aktiv,
    wert: ergebnis.push.aktiv ? "aktiv" : `deaktiviert (${ergebnis.push.grund ?? "unbekannt"})`,
  });

  if (!ergebnis.push.ergebnisse.length) {
    befunde.push({
      station: "Zustellung an Abos",
      ok: false,
      wert: "kein einziges Abo angesprochen",
      hinweis: "Für diesen Nutzer und Mandanten ist kein Abo hinterlegt. kmvsPush.erlauben() ausführen.",
    });
    if (ergebnis.push.aktiv) probleme.push("Es ist kein Push-Abo hinterlegt — die Mitteilung erscheint nur in der Anwendung.");
  }
  for (const z of ergebnis.push.ergebnisse) {
    befunde.push({
      station: `Zustellung → ${z.anbieter}`,
      ok: z.ok,
      wert: z.ok ? `angenommen (${z.endpoint})` : `HTTP ${z.statusCode ?? "?"} — ${z.fehler ?? ""}`,
      hinweis: z.entfernt
        ? "Dieses Abo existiert beim Push-Dienst nicht mehr und wurde entfernt (altes Gerät/Profil)."
        : z.ok
          ? undefined
          : deutungZuStatus(z.statusCode),
    });
    if (!z.ok && !z.entfernt) probleme.push(`Der Push-Dienst ${z.anbieter} lehnte die Zustellung ab (HTTP ${z.statusCode ?? "?"}).`);
  }

  const angenommen = ergebnis.push.ergebnisse.some((z) => z.ok);
  if (angenommen) {
    console.log(
      "%c[kmvsPush] Mindestens ein Push-Dienst hat die Mitteilung angenommen. Erscheint trotzdem keine Einblendung, liegt es am Betriebssystem (Fokus/Nicht stören) — kmvsPush.lokalerTest() grenzt das ein.",
      STIL_NEUTRAL,
    );
  }
  ausgeben("Benachrichtigungen — Testmitteilung", befunde, probleme);
  return ergebnis;
}

const deutungZuStatus = (status: number | null): string | undefined => {
  switch (status) {
    case 400:
    case 401:
    case 403:
      return "Der Push-Dienst weist die Absenderkennung ab — VAPID_PUBLIC_KEY/VAPID_PRIVATE_KEY im Backend passen nicht zum Abo, oder VAPID_SUBJECT ist ungültig.";
    case 404:
    case 410:
      return "Das Abo gibt es beim Push-Dienst nicht mehr.";
    case 413:
      return "Die Nachricht ist zu groß für den Push-Dienst.";
    case 429:
      return "Der Push-Dienst drosselt (zu viele Zustellungen).";
    default:
      return status && status >= 500 ? "Vorübergehende Störung beim Push-Dienst." : undefined;
  }
};

function hilfe(): void {
  console.group("%cKMVS — Benachrichtigungen prüfen", STIL_TITEL);
  console.log("kmvsPush.pruefen()       Alle Stationen prüfen und Befund ausgeben");
  console.log("kmvsPush.test()          Echte Testmitteilung: Mitteilungszentrum + Browser-Push (mit Zustellergebnis je Abo)");
  console.log("kmvsPush.lokalerTest()   Einblendung direkt über den Service Worker — ohne Server, ohne Push-Dienst");
  console.log("kmvsPush.erlauben()      Berechtigung anfragen und Abo anlegen");
  console.log("kmvsPush.reparieren()    Abo verwerfen und frisch anlegen (alter Schlüssel, unbekanntes Abo)");
  console.log("%cÜbliche Reihenfolge: pruefen() → erlauben() → test() → bei fehlender Einblendung lokalerTest()", STIL_NEUTRAL);
  console.groupEnd();
}

export const pushDiagnostics = { hilfe, pruefen, test, lokalerTest, erlauben, reparieren };

declare global {
  interface Window {
    kmvsPush?: typeof pushDiagnostics;
  }
}

/** Hängt das Werkzeug ans `window` — einmalig beim App-Start aufgerufen. */
export const installPushDiagnostics = (): void => {
  if (typeof window === "undefined") return;
  window.kmvsPush = pushDiagnostics;
  console.log(
    "%cKMVS%c Benachrichtigungen prüfen: %ckmvsPush.hilfe()",
    "background:#7d2169;color:#fff;padding:1px 5px;border-radius:3px;font-weight:700",
    "color:inherit",
    "font-weight:700",
  );
};
